import { Link } from "react-router-dom";
import { Bike, PersonStanding, Waves, Dumbbell, Activity, Moon } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { fmtDate } from "@/lib/dates";
import { fmtDuration, SPORT_LABELS } from "@/lib/tss";
import type { WorkoutSummary } from "@/types";
import { DeviceAttribution } from "@/components/DeviceAttribution";

const SPORT_ICON: Record<string, typeof Bike> = {
  swim: Waves,
  bike: Bike,
  run: PersonStanding,
  brick: Activity,
  strength: Dumbbell,
  mobility: Moon,
  rest: Moon,
};

const SPORT_TINT: Record<string, string> = {
  swim: "bg-navy-soft/15 text-navy-soft",
  bike: "bg-amber/15 text-amber-dark",
  run: "bg-coral/15 text-coral",
  brick: "bg-navy/15 text-navy-deep",
  strength: "bg-taupe/20 text-taupe",
  mobility: "bg-cream-border text-taupe",
  rest: "bg-cream-border text-taupe",
};

interface Props {
  workout: WorkoutSummary;
}

export function WorkoutRow({ workout }: Props) {
  const Icon = SPORT_ICON[workout.sport] ?? Activity;
  const tint = SPORT_TINT[workout.sport] ?? "bg-cream-border text-taupe";
  return (
    <Link to={`/workout/${workout.id}`}>
      <Card className="flex items-center gap-3 py-3 hover:border-amber/40">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tint}`}>
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-navy-deep">{workout.title}</div>
          <div className="mt-0.5 flex flex-wrap gap-x-2 text-[12px] text-taupe">
            <span>{fmtDate(workout.date, "d MMM")}</span>
            <span>·</span>
            <span>{SPORT_LABELS[workout.sport] ?? workout.sport}</span>
            {workout.duration_min != null ? (
              <>
                <span>·</span>
                <span>{fmtDuration(workout.duration_min)}</span>
              </>
            ) : null}
            {workout.distance_km != null ? (
              <>
                <span>·</span>
                <span>{workout.distance_km} км</span>
              </>
            ) : null}
            {workout.device_name ? (
              <DeviceAttribution deviceName={workout.device_name} compact />
            ) : null}
          </div>
        </div>
        {workout.tss != null ? (
          <div className="shrink-0 text-right">
            <div className="metric-number text-[15px]">{workout.tss}</div>
            <div className="text-[10px] uppercase tracking-wider text-taupe">TSS</div>
          </div>
        ) : null}
      </Card>
    </Link>
  );
}
